package com.arenaproplay.admin.ui.components.forms

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.AddressSuggestion
import com.arenaproplay.admin.domain.admin.PlaceAddress
import com.arenaproplay.admin.ui.map.LeafletMap
import com.arenaproplay.admin.ui.map.reverseGeocode
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import io.ktor.http.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.w3c.fetch.Response
import kotlin.math.abs
import kotlin.random.Random

/** What the user finally picked: address text plus its coordinates. */
data class ResolvedAddress(
    val address: String,
    val latitude: Double,
    val longitude: Double,
    /** Present only when the pick came through the Google Places proxy. */
    val city: String? = null,
    val country: String? = null,
)

/** DOM id of the picker map container (one map is shown at a time). */
private const val MAP_CONTAINER_ID = "club-address-map"

private data class SuggestionItem(
    val title: String,
    val subtitle: String,
    val placeId: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
)

/**
 * Address input with autocomplete: backend Google Places proxy first,
 * Nominatim directly when the proxy has no key / fails. Picking a suggestion
 * calls [onResolved] with the address text and coordinates; free typing only
 * calls [onValueChange] (coordinates stay unset).
 */
@Composable
fun AddressAutocompleteField(
    value: String,
    onValueChange: (String) -> Unit,
    onResolved: (ResolvedAddress) -> Unit,
    autocomplete: suspend (query: String, session: String?) -> List<AddressSuggestion>,
    resolvePlace: suspend (placeId: String, session: String?) -> PlaceAddress,
    label: String? = "Address",
    placeholder: String = "Start typing an address…",
    /** Current coordinates — the picker map opens centred here. */
    latitude: Double? = null,
    longitude: Double? = null,
) {
    val scope = rememberCoroutineScope()
    var suggestions by remember { mutableStateOf<List<SuggestionItem>>(emptyList()) }
    var open by remember { mutableStateOf(false) }
    var session by remember { mutableStateOf<String?>(null) }
    var lastPicked by remember { mutableStateOf<String?>(null) }
    // The last centre the map itself reported — lets us tell a user drag (skip
    // recenter) from an external coordinate change (autocomplete pick → recenter).
    var lastMapCoords by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    var mapReverseJob by remember { mutableStateOf<Job?>(null) }

    fun currentSession(): String =
        session ?: buildString(32) { repeat(32) { append("0123456789abcdef"[Random.nextInt(16)]) } }
            .also { session = it }

    LaunchedEffect(value) {
        if (value == lastPicked) return@LaunchedEffect
        val query = value.trim()
        if (query.length < 3) {
            suggestions = emptyList()
            open = false
            return@LaunchedEffect
        }
        delay(350)
        val fromProxy = try {
            autocomplete(query, currentSession())
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            emptyList()
        }
        val items =
            if (fromProxy.isNotEmpty()) {
                fromProxy.map { SuggestionItem(it.title, it.subtitle.orEmpty(), placeId = it.placeId) }
            } else {
                nominatimSearch(query)
            }
        suggestions = items
        open = items.isNotEmpty()
    }

    Box(Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
        Column(Modifier.fillMaxWidth()) {
            TextInput(
                value,
                { onValueChange(it) },
                label = label,
                placeholder = placeholder,
            )
        }
        if (open) {
            // Invisible overlay: any outside click closes the dropdown.
            Box(
                Modifier.styleModifier {
                    property("position", "fixed")
                    property("inset", "0")
                    property("z-index", "90")
                }.onClick { open = false }
            )
            Column(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "100%")
                        property("left", "0")
                        property("margin-top", "4px")
                        property("background", "#FFFFFF")
                        property("border", "1px solid rgba(15,18,34,0.10)")
                        property("border-radius", "10px")
                        property("box-shadow", "0 8px 24px -8px rgba(15,18,34,0.18)")
                        property("z-index", "91")
                        property("overflow", "hidden")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                    }
            ) {
                suggestions.forEach { item ->
                    Column(
                        Modifier.fillMaxWidth()
                            .padding(topBottom = 9.px, leftRight = 14.px)
                            .cursor(Cursor.Pointer)
                            .styleModifier { property("transition", "background 120ms ease") }
                            .onClick {
                                open = false
                                scope.launch {
                                    val resolved: ResolvedAddress? = when {
                                        item.placeId != null -> try {
                                            val place = resolvePlace(item.placeId, session)
                                            session = null
                                            val joined = listOfNotNull(
                                                place.street, place.houseNumber, place.city, place.country,
                                            ).filter { it.isNotBlank() }.joinToString(", ")
                                            ResolvedAddress(
                                                address = place.formattedAddress
                                                    ?: joined.ifBlank { item.title },
                                                latitude = place.latitude,
                                                longitude = place.longitude,
                                                city = place.city,
                                                country = place.country,
                                            )
                                        } catch (_: Throwable) {
                                            null
                                        }

                                        item.lat != null && item.lng != null ->
                                            ResolvedAddress(item.title, item.lat, item.lng)

                                        else -> null
                                    }
                                    if (resolved != null) {
                                        lastPicked = resolved.address
                                        onResolved(resolved)
                                    }
                                }
                            },
                    ) {
                        SpanText(item.title, Modifier.bodyMRegular().color(Color("#0F1222")))
                        if (item.subtitle.isNotBlank()) {
                            SpanText(item.subtitle, Modifier.bodySMedium().color(Color("#5D6472")))
                        }
                    }
                }
            }
        }
    }

    run {
        Box(Modifier.height(10.px))
        SpanText(
            "Drag the map so the pin sits exactly on the club",
            Modifier.bodySMedium().color(Color("#5D6472")),
        )
        Box(Modifier.height(6.px))
        Box(
            Modifier.fillMaxWidth()
                .height(240.px)
                .id(MAP_CONTAINER_ID)
                .styleModifier {
                    property("border-radius", "10px")
                    property("overflow", "hidden")
                    property("border", "1px solid rgba(15,18,34,0.10)")
                    property("flex-shrink", "0")
                },
        ) {}

        LaunchedEffect(Unit) {
            delay(200)
            val la = latitude
            val lo = longitude
            if (la != null && lo != null) {
                LeafletMap.init(MAP_CONTAINER_ID, la, lo, zoom = 16)
            } else {
                // Neutral default (geographic centre of Ukraine) until a point is set.
                LeafletMap.init(MAP_CONTAINER_ID, 48.3794, 31.1656, zoom = 5)
            }
            LeafletMap.onMoveEnd { centerLat, centerLng ->
                lastMapCoords = centerLat to centerLng
                // Dragging the map re-picks the location — reverse-geocode the new
                // centre into the address field (debounced for Nominatim's 1 req/s).
                mapReverseJob?.cancel()
                mapReverseJob = scope.launch {
                    delay(600)
                    val r = reverseGeocode(centerLat, centerLng)
                    val text = r?.formattedAddress ?: value
                    // Mark as picked so the value-watching effect doesn't re-query.
                    lastPicked = text
                    onResolved(
                        ResolvedAddress(
                            address = text,
                            latitude = centerLat,
                            longitude = centerLng,
                            city = r?.city,
                            country = r?.country,
                        ),
                    )
                }
            }
        }
        // Recenter when coordinates change externally (autocomplete pick). A change
        // the map itself just reported is skipped so a drag can't yank the zoom.
        LaunchedEffect(latitude, longitude) {
            val la = latitude
            val lo = longitude
            if (la != null && lo != null) {
                val reported = lastMapCoords
                val fromMap = reported != null &&
                    abs(reported.first - la) < 1e-6 && abs(reported.second - lo) < 1e-6
                if (!fromMap) LeafletMap.setView(la, lo, zoom = 16)
            }
        }
        DisposableEffect(Unit) { onDispose { LeafletMap.destroy() } }
    }
}

/** Direct Nominatim search — fallback when the backend proxy has no Google key. */
private suspend fun nominatimSearch(query: String): List<SuggestionItem> = try {
    val url = "https://nominatim.openstreetmap.org/search" +
        "?format=json&q=${query.encodeURLQueryComponent()}&limit=8&accept-language=en"
    val response: Response = window.fetch(url).await()
    if (!response.ok) {
        emptyList()
    } else {
        val results: dynamic = JSON.parse<dynamic>(response.text().await())
        val list = mutableListOf<SuggestionItem>()
        val length = (results.length as? Int) ?: 0
        for (i in 0 until length) {
            val item: dynamic = results[i]
            val displayName = item.display_name as? String ?: continue
            val lat = (item.lat as? String)?.toDoubleOrNull() ?: continue
            val lng = (item.lon as? String)?.toDoubleOrNull() ?: continue
            list.add(SuggestionItem(title = displayName, subtitle = "", lat = lat, lng = lng))
        }
        list
    }
} catch (e: CancellationException) {
    throw e
} catch (_: Throwable) {
    emptyList()
}
